import axios from "axios";
import toast from "react-hot-toast";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, AppState } from "@/store/configureStore";
import { CREATE_GROUP_CART_API } from "@/constants";
import {
	createGroupCart,
	updateGroupCartDetails,
	updateGroupCartInvitees,
} from "@/store/slices/groupCartSlice";
import { groupCartProps } from "@/interface/cart";
import { useRouter } from "next/navigation";
import { FetchAllGroupCarts, useGroupCartProduct } from "..";

const useShopWithFriends = () => {
	const router = useRouter();
	const dispatch = useDispatch<AppDispatch>();
	const groupCart = useSelector((state: AppState) => state.group_cart);
	const initialCartItems = useSelector((state: AppState) => state?.cart?.items);
	const { addItemToGroupCart } = useGroupCartProduct();

	const user = useSelector((state: AppState) => state.user);
	const activeCart = user?.groupCarts?.find(
		(groupcart: groupCartProps) => groupcart?.id === groupCart?.id
	);
	const cartId = groupCart?.id || "";

	const inviteFriendsSchema = yup.object({
		invitee: yup
			.string()
			.email("Invalid email address")
			.required("Email is required"),
	});

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		clearErrors,
		reset,
		resetField,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(inviteFriendsSchema),
	});

	const createNewGroupCart = async () => {
		try {
			const response = await axios.post(
				CREATE_GROUP_CART_API,
				{},
				{
					headers: {
						Authorization: `Bearer ${user?.token}`,
					},
				}
			);

			dispatch(
				updateGroupCartDetails({
					id: response?.data?.result?.id,
					totalPrice: response?.data?.result?.totalPrice,
					allowShopping: response?.data?.result?.allowShopping,
					owner: response?.data?.result?.owner,
				})
			);

			// FetchAllGroupCarts();

			return response?.data?.result?.id;
		} catch (error: any) {
			// console.error("Error creating group cart:", error);
			toast.remove();
			toast.error(error?.response?.data?.error);
			return;
		}
	};

	const deleteGroupCart = async () => {
		const endpoint = `${CREATE_GROUP_CART_API}/${cartId}/delete`;
		try {
			const response = await axios.delete(endpoint, {
				headers: {
					Authorization: `Bearer ${user?.token}`,
				},
			});
			// console.log(response?.data);
			toast.remove();
			toast.success("Cart has been deleted");
			FetchAllGroupCarts();
		} catch (error: any) {
			// console.log("API Error: ", error);
			toast.remove();
			toast.error(error?.response?.data?.error);
		}
	};

	const inviteFriends = async (emails: string[], onClose: () => void) => {
		const groupcartId = groupCart.id || (await createNewGroupCart());

		const endpoint = `${CREATE_GROUP_CART_API}/${groupcartId}/invite`;
		try {
			const response = await axios.post(
				endpoint,
				{
					emails: emails.map(email => ({ email })), // Transform array of strings to array of objects
				},
				{
					headers: {
						Authorization: `Bearer ${user?.token}`,
					},
				}
			);
			toast.remove();
			toast.success("Invite sent succesfully");

			dispatch(
				updateGroupCartInvitees({
					invitees: response?.data?.result?.invitees,
				})
			);

			onClose();
		} catch (error: any) {
			console.log("API Error: ", error);
			toast.remove();
			toast.error(error?.response?.data?.error);
		}
	};

	const removeFriends = async (email: string | undefined) => {
		const endpoint = `${CREATE_GROUP_CART_API}/${cartId}/remove-invitee`;
		try {
			const response = await axios.delete(endpoint, {
				headers: {
					Authorization: `Bearer ${user?.token}`,
				},
				params: {
					email: email,
				},
			});
			reset();
			dispatch(
				updateGroupCartInvitees({
					invitees: response?.data?.result?.invitees,
				})
			);
			toast.remove();
			toast.success("Invitee successfully deleted");
		} catch (error: any) {
			// console.log("API Error", error);
			toast.remove();
			toast.error(`${error?.response?.data?.error} Try again!`);
		}
	};

	return {
		createNewGroupCart,
		deleteGroupCart,
		removeFriends,
		inviteFriends,
		register,
		handleSubmit,
		errors,
	};
};

export default useShopWithFriends;
